import React, { useEffect } from 'react';
import './BlogPost.css';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function QueEsAgenteIA() {
    const navigate = useNavigate();

    // Esto hace que al entrar al blog, la página empiece arriba del todo
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="blog-post-container">
            {/* --- INICIO DE SEO Y REDES SOCIALES --- */}
            <Helmet>
                {/* 1. Título que sale en la pestaña del navegador y en Google */}
                <title>¿Qué es un Agente de IA y por qué lo necesitas? | Navi</title>

                {/* 2. Descripción para Google (lo que sale debajo del link azul) */}
                <meta name="description" content="Descubre la diferencia entre un chatbot y un Agente de IA. Aumenta la productividad de tu Pyme y reduce costes con la nueva era de la automatización." />

                {/* 3. OPEN GRAPH (Para LinkedIn, WhatsApp, Facebook) */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content="¿Qué es un Agente de IA y por qué tu empresa lo necesita?" />
                <meta property="og:description" content="Olvida los chatbots. Los agentes autónomos son el futuro de la logística y la atención al cliente. Lee el artículo completo." />

                <meta property="og:image" content="https://www.navitechlabs.com/imagenes_blogs/miniatura_blog1.png" />

                {/* URL de este artículo */}
                <meta property="og:url" content="https://www.navitechlabs.com/blogs/que-es-agente-ia" />
                <meta property="og:site_name" content="Navitech Labs" />
            </Helmet>
            {/* --- ENCABEZADO DEL ARTÍCULO --- */}
            <header className="blog-header">
                <span className="blog-category">INTELIGENCIA ARTIFICIAL</span>
                <h1 className="blog-title">¿Qué es un Agente de IA y por qué tu empresa lo necesita urgentemente?</h1>
                <h2 className="blog-subtitle">
                    Dejamos atrás la era de los chatbots básicos. Descubre cómo los agentes autónomos están redefiniendo la productividad en las PYMES españolas en 2025.
                </h2>

                <div className="blog-meta">
                    <div className="author-info">
                        <img src="/assets/franco_miembro.jpeg" alt="Avatar" className="author-avatar" /> {/* Puedes poner tu foto aquí */}
                        <div>
                            <p className="author-name">Por <strong>Franco Giner</strong></p>
                            <p className="post-date">Jueves, 11 de Diciembre de 2025 · 5 min de lectura</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* --- IMAGEN PRINCIPAL (HERO) --- */}
            <div className="blog-hero-image">
                {/* Asegúrate de tener esta imagen en public/imagenes/ */}
                <img src="/imagenes_blogs/imagen1_blog1.png" alt="Futuro de la IA en empresas" />
            </div>

            {/* --- CONTENIDO DEL ARTÍCULO --- */}
            <div className="blog-content">
                <p>
                    Si has intentado digitalizar tu empresa en los últimos años, seguramente te has topado con los
                    "chatbots" tradicionales. Esos asistentes torpes que solo sabían responder a preguntas predefinidas
                    y que, ante la mínima duda, frustraban al cliente.
                </p>
                <p>
                    Pero el 2025 ha marcado un punto de inflexión. <strong>El chatbot ha muerto; larga vida al Agente de
                    IA.</strong>
                </p>

                <h3>La diferencia clave: "Responder" vs. "Actuar"</h3>
                <p>
                    Para entender por qué esto es una revolución, debemos definir qué es un Agente de IA. A diferencia
                    de un modelo de lenguaje simple (como el ChatGPT de hace unos años) que solo genera texto,
                    un <strong>Agente tiene capacidad de agencia</strong>.
                </p>
                <p>
                    Un Agente percibe, razona, utiliza herramientas y <strong>ejecuta acciones</strong> para cumplir un
                    objetivo.
                </p>

                <blockquote className="blog-quote">
                    "El software actual es bastante tonto... Pero en el futuro cercano, cualquiera podrá tener un
                    asistente personal impulsado por IA que va mucho más allá de la tecnología actual. A este tipo de
                    software lo llamamos 'Agente'."
                    <footer style={{marginTop: '15px', fontSize: '1rem', fontWeight: '600', color: '#333'}}>
                        — Bill Gates (Fundador de Microsoft)
                    </footer>
                </blockquote>
                <p>
                    El concepto de Agente de IA no es nuevo; lleva años siendo la ambición de los líderes tecnológicos,
                    pero hoy ha dejado de ser teoría para convertirse en una realidad accesible.
                </p>
                <h3>Los datos hablan: El impacto en las PYMES</h3>
                <p>
                    No se trata solo de tecnología futurista, se trata de rentabilidad hoy. Según el último informe
                    de <em>McKinsey & Company (2025)</em> sobre automatización global:
                </p>
                <ul>
                    <li><strong>40% de aumento en productividad:</strong> Las empresas que integran agentes para tareas
                        repetitivas liberan casi la mitad del tiempo de su plantilla humana.
                    </li>
                    <li><strong>Reducción de costes operativos:</strong> Se estima una bajada del 30% en gastos de
                        atención al cliente al primer nivel.
                    </li>
                    <li><strong>Satisfacción del cliente (CSAT):</strong> La inmediatez ya no es un lujo, es una
                        exigencia. El 82% de los consumidores esperan respuestas en menos de 10 minutos.
                    </li>
                </ul>

                {/* --- IMAGEN SECUNDARIA --- */}
                <figure className="blog-image-wrapper">

                    <img src="/imagenes_blogs/imagen2_blog1.png" alt="Gráfico de productividad con IA"/>
                    <figcaption>Comparativa de eficiencia: Flujo tradicional vs. Flujo con Agentes IA.</figcaption>
                </figure>

                <h3>Casos de uso reales en España</h3>
                <p>
                    En <strong>Navi</strong>, no hablamos de teoría. Vemos cómo esto transforma negocios en España y en
                    el resto del mundo a diario.
                </p>
                <h4>1. Logística Inteligente</h4>
                <p>
                    Imagina un agente que no solo rastrea un paquete. Si hay un retraso, el agente contacta
                    proactivamente al cliente por WhatsApp, le pide disculpas, le ofrece una nueva hora de entrega y
                    actualiza la ruta del conductor en tiempo real. Sin intervención humana.
                </p>
                <h4>2. Gestión de Citas y Reservas</h4>
                <p>
                    Para clínicas o consultorías, el agente negocia huecos libres con el cliente, cierra la cita y la
                    añade al Google Calendar del doctor, enviando recordatorios automáticos para evitar el ausentismo.
                </p>

                <h3>¿Por qué necesitas uno ahora?</h3>
                <p>
                    La barrera de entrada ha caído. Antes, esta tecnología solo estaba al alcance de multinacionales.
                    Hoy, gracias a soluciones como las de <strong>Navi</strong>, una PYME puede tener un sistema de
                    automatización de nivel empresarial por una fracción del coste de un empleado administrativo.
                </p>
                <p>
                    La pregunta ya no es si vas a implementar IA en tu negocio, sino si lo harás antes que tu
                    competencia.
                </p>

                <div className="blog-cta-box">
                    <h3>¿Listo para dar el salto?</h3>
                    <p>En Navi te ayudamos a crear tu primer Agente de IA personalizado.</p>

                    <button
                        className="cta-button"
                        onClick={() => window.open(
                            'https://wa.me/34624118531?text=%C2%A1Hola%20Me%20interesa%20conocer%20m%C3%A1s%20sobre%20c%C3%B3mo%20Navi%20puede%20ayudarme%20a%20digitalizar%20y%20optimizar%20mis%20procesos%20autom%C3%A1ticos.',
                            '_blank'
                        )}
                    >
                        Hablar con un experto de Navi
                    </button>
                </div>

            </div>
        </div>
    );
}